import { useReactFlow } from '@xyflow/react';
import type { IfOperator, NodeSettingsProps } from '../../types';
import { useFlowDisplayLanguage, type FlowDisplayLanguage } from '../../displayLanguage';
import { normalizeNumericInput } from '../../normalizeNumericInput';

export const IF_OPERATOR_LABELS: Record<FlowDisplayLanguage, Record<IfOperator, string>> = {
  en: {
    '<': '< Less than',
    '<=': '<= Less than or equal to',
    '=': '= Equal to',
    '=>': '=> Greater than or equal to',
    '>': '> Greater than',
    '≠': '≠ Not equal to',
  },
  ja: {
    '<': '< より小さい',
    '<=': '<= 以下',
    '=': '= 等しい',
    '=>': '=> 以上',
    '>': '> より大きい',
    '≠': '≠ 等しくない',
  },
};

const OPERATORS: IfOperator[] = ['<', '<=', '=', '=>', '>', '≠'];

export function IfSettings({ node }: NodeSettingsProps<'if'>) {
  const { updateNodeData } = useReactFlow();
  const displayLanguage = useFlowDisplayLanguage();
  const operatorLabels = IF_OPERATOR_LABELS[displayLanguage];

  return (
    <div className="space-y-4">
      <label className="block" data-product-tour="card-input-1">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'condition operator' : '比較の条件'}
        </span>
        <select
          value={node.data.operator}
          onChange={(event) => updateNodeData(node.id, { operator: event.target.value as IfOperator })}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal-400"
        >
          {OPERATORS.map((operator) => (
            <option key={operator} value={operator}>{operatorLabels[operator]}</option>
          ))}
        </select>
      </label>
      <label className="block" data-product-tour="card-input-2">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {displayLanguage === 'en' ? 'value to compare' : '比較する値'}
        </span>
        <input
          type="text"
          value={node.data.compareValue}
          onChange={(event) => updateNodeData(node.id, { compareValue: event.target.value })}
          onBlur={(event) => {
            const raw = event.currentTarget.value;
            const normalized = normalizeNumericInput(raw);
            if (normalized !== raw && normalized.trim() !== '' && Number.isFinite(Number(normalized))) {
              updateNodeData(node.id, { compareValue: normalized });
            }
          }}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal-400"
        />
      </label>
    </div>
  );
}
